import { type HeadersFunction, json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import { backfillVariantStock, loadProductAssociations, loadProductBreadcrumbPath } from '~/api/product.server';
import ProductPage from '~/components/ProductPage';
import type { ApiProduct, Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';
import { publicPageHeaders } from '~/utils/cacheControl.server';
import { buildSeoMeta, getRootOrigin, getShopName } from '~/utils/meta';
import { buildProductJsonLd } from '~/utils/productJsonLd';
import { resolveRoutePath } from '~/utils/routePath.server';

export interface ProductPageLoaderData {
  product: ApiProduct;
  associations: { title: string; products: Product[] }[];
  breadcrumbPath: { name: string; slug: string }[];
  productJsonLd: Record<string, unknown>;
  preselectedVariantCode: string | null;
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const slug = params.slug!;
  const API_URL = process.env.PUBLIC_API_URL;

  // Fetch product with all expanded data
  const productRes = await apiFetch(`${API_URL}/api/v2/shop/products-by-slug/${slug}`, {}, { request });
  if (!productRes.ok) throw new Response('Product not found', { status: 404 });
  const product: ApiProduct = await productRes.json();

  product.variants = await backfillVariantStock(product, API_URL!, request);

  // A `?variant=<code>` query param deep-links to a specific variant combination.
  // Resolve it against this product's variants server-side so the initial render can
  // preselect it without a hydration mismatch; unknown codes fall through to null
  // (the client then strips the stale param from the URL).
  const requestedVariantCode = new URL(request.url).searchParams.get('variant');
  const preselectedVariantCode =
    requestedVariantCode && product.variants.some((v) => v.code === requestedVariantCode) ? requestedVariantCode : null;

  // Associations and breadcrumbs are shared with the admin draft preview (ProductPreviewPage)
  // so the two pages cannot drift — see app/api/product.server.ts.
  const [associations, breadcrumbPath] = await Promise.all([
    loadProductAssociations(product, API_URL!, request),
    loadProductBreadcrumbPath(product, API_URL!, request),
  ]);

  // schema.org/Product structured data. Built here rather than in the component so it
  // reflects the default variant (not whatever the shopper later selects) and can carry
  // an absolute product URL — origin from the request, locale-prefixed path from the route.
  const { routePath } = resolveRoutePath(request);
  const origin = new URL(request.url).origin;
  const productJsonLd = buildProductJsonLd(product, {
    url: `${origin}${routePath('product', product.slug)}`,
    category: breadcrumbPath.at(-1)?.name,
  });

  return json({
    product,
    associations,
    breadcrumbPath,
    productJsonLd,
    preselectedVariantCode,
  });
}

export const headers: HeadersFunction = ({ parentHeaders }) => publicPageHeaders(parentHeaders);

export const meta: MetaFunction<typeof loader> = ({ data, matches, location }) => {
  const product = data?.product;
  // Stand-in until the backend computes `metaDescription` for products — right now it
  // sends null for every one of them, and dropping this would leave them with no
  // description at all.
  const descText = (() => {
    if (!product?.shortDescription || !Array.isArray(product.shortDescription)) return '';
    const html = product.shortDescription.find((i) => i.code === 'monsieurbiz.html') as
      | { code: string; data: { content: string } }
      | undefined;
    return html?.data?.content?.replace(/<[^>]*>/g, '').trim() || '';
  })();

  const seoMeta = buildSeoMeta({
    seo: product,
    fallbackTitle: product?.name || 'Produkt',
    fallbackDescription: descText,
    origin: getRootOrigin(matches),
    shopName: getShopName(matches),
    path: location.pathname,
    ogType: 'product',
  });

  // An inactive product is hidden from listings and search but deliberately still
  // reachable by direct URL, so the page answers 200 — this tag is the only thing that
  // keeps it out of the index, and the only thing that gets it dropped again once it has
  // been indexed. `follow`, not `nofollow`: everything it links to is indexable, and a
  // blocked page is never fetched, so the tag would never be read. See `Robots.tsx`.
  if (product?.active === false) {
    return [...seoMeta, { name: 'robots', content: 'noindex, follow' }];
  }

  return seoMeta;
};

export default function ProductPageRoute() {
  const { productJsonLd, ...data } = useLoaderData<typeof loader>() as ProductPageLoaderData;
  return (
    <>
      <script
        type="application/ld+json"
        // Escape "<" so any HTML that slipped through the product data can never close the
        // script element early. Same guard the root uses for its Organization JSON-LD.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd).replace(/</g, '\\u003c') }}
      />
      <ProductPage {...data} />
    </>
  );
}
